/**
 * AiAssistButton -- Small icon button that triggers AI assistance.
 *
 * Renders as a subtle ghost/secondary button with a sparkle icon.
 * Shows a spinner when loading. Designed to sit inline next to form
 * labels without dominating the visual hierarchy.
 *
 * ADR-022: UI component in components/cms/ (domain-specific).
 * @see ADR-055 for AI-assisted resume feature architecture.
 */

interface AiAssistButtonProps {
  /** Click handler to trigger the AI request. */
  onClick: () => void;
  /** Whether an AI request is currently in flight. */
  isLoading: boolean;
  /** Disable the button (e.g. when required input is empty). */
  disabled?: boolean;
  /** Tooltip and aria-label text. Defaults to "AI Assist". */
  label?: string;
  /** Visual size variant. Defaults to 'sm'. */
  size?: 'sm' | 'md';
}

export function AiAssistButton({
  onClick,
  isLoading,
  disabled = false,
  label = 'AI Assist',
  size = 'sm',
}: AiAssistButtonProps) {
  const sizeClass = size === 'md' ? 'ai-assist-btn--md' : 'ai-assist-btn--sm';

  return (
    <button
      type="button"
      className={`ai-assist-btn ${sizeClass} ${isLoading ? 'ai-assist-btn--loading' : ''}`}
      onClick={onClick}
      disabled={disabled || isLoading}
      title={label}
      aria-label={label}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <span className="ai-assist-btn__spinner" aria-hidden="true" />
      ) : (
        <svg
          className="ai-assist-btn__icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8 1l1.5 3.5L13 6l-3.5 1.5L8 11 6.5 7.5 3 6l3.5-1.5L8 1z"
            fill="currentColor"
          />
          <path
            d="M12.5 9l.75 1.75L15 11.5l-1.75.75L12.5 14l-.75-1.75L10 11.5l1.75-.75L12.5 9z"
            fill="currentColor"
            opacity="0.6"
          />
          <path
            d="M3.5 10l.5 1.25L5.25 11.75l-1.25.5L3.5 13.5l-.5-1.25L1.75 11.75l1.25-.5L3.5 10z"
            fill="currentColor"
            opacity="0.4"
          />
        </svg>
      )}
    </button>
  );
}
